import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  FileText, 
  Mail, 
  TrendingUp,
  Eye,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Package,
  FolderOpen,
  Plus,
  Edit,
  Trash2,
  X,
  LogOut,
  Lock,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [authToken, setAuthToken] = useState(localStorage.getItem('adminToken') || '');
  
  const [analytics, setAnalytics] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '', image: '' });
  const [productForm, setProductForm] = useState({
    name: '', slug: '', category_id: '', description: '',
    specifications: {}, images: [''], min_order: '', origin: '', certifications: []
  });

  // Check if already authenticated
  useEffect(() => {
    const verifyTokenOnMount = async () => {
      if (!authToken) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/admin/verify?token=${authToken}`);
        if (res.data.valid) {
          setIsAuthenticated(true);
          fetchData();
        } else {
          localStorage.removeItem('adminToken');
          setAuthToken('');
          setLoading(false);
        }
      } catch (error) {
        localStorage.removeItem('adminToken');
        setAuthToken('');
        setLoading(false);
      }
    };
    verifyTokenOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/admin/verify?token=${authToken}`);
      if (res.data.valid) {
        setIsAuthenticated(true);
        fetchData();
      } else {
        localStorage.removeItem('adminToken');
        setAuthToken('');
        setLoading(false);
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      setAuthToken('');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('adminToken', res.data.token);
      setAuthToken(res.data.token);
      setIsAuthenticated(true);
      fetchData();
    } catch (error) {
      setLoginError('Invalid credentials. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthToken('');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, rfqsRes, contactsRes, categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/rfqs`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/admin/products`)
      ]);
      setAnalytics(analyticsRes.data);
      setRfqs(rfqsRes.data);
      setContacts(contactsRes.data);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, categoryForm);
      } else {
        await axios.post(`${API}/admin/categories`, categoryForm);
      }
      setShowCategoryModal(false);
      setCategoryForm({ name: '', slug: '', description: '', image: '' });
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await axios.delete(`${API}/admin/categories/${id}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  // Product CRUD
  const handleSaveProduct = async () => {
    try {
      const productData = {
        ...productForm,
        images: productForm.images.filter(img => img.trim() !== ''),
        specifications: typeof productForm.specifications === 'string' 
          ? JSON.parse(productForm.specifications) 
          : productForm.specifications,
        certifications: typeof productForm.certifications === 'string'
          ? productForm.certifications.split(',').map(c => c.trim())
          : productForm.certifications
      };
      
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData);
      } else {
        await axios.post(`${API}/admin/products`, productData);
      }
      setShowProductModal(false);
      setProductForm({
        name: '', slug: '', category_id: '', description: '',
        specifications: {}, images: [''], min_order: '', origin: '', certifications: []
      });
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save product:', error);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${API}/admin/products/${id}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete product:', error);
      }
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-border p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
          <p className="text-3xl font-heading font-bold text-primary-900">{value}</p>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 flex items-center justify-center ${color === 'secondary' ? 'bg-secondary-500' : 'bg-[#0a1628]'}`}>
          <Icon className={`w-6 h-6 ${color === 'secondary' ? 'text-primary-900' : 'text-white'}`} />
        </div>
      </div>
    </motion.div>
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#0a1628] mx-auto mb-4 flex items-center justify-center">
                <Lock className="w-8 h-8 text-secondary-500" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-primary-900">Admin Login</h1>
              <p className="text-muted-foreground text-sm mt-2">Enter your credentials to access the dashboard</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 rounded-none border-border"
                    placeholder="Enter username"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 rounded-none border-border"
                    placeholder="Enter password"
                    required
                  />
                </div>
              </div>
              
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {loginError}
                </div>
              )}
              
              <Button
                type="submit"
                className="w-full bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-none py-6 text-sm font-bold uppercase tracking-wider"
              >
                Sign In
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                Admin credentials are required to access this area.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f9f7] pt-20 sm:pt-24 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-900 mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f9f7]" data-testid="admin-page">
      {/* Header */}
      <section className="bg-[#0a1628] pt-20 sm:pt-24">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-secondary-400 mb-2">
                Super Admin Dashboard
              </p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-white">
                {t('admin.title')}
              </h1>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={fetchData}
                className="bg-secondary-500 hover:bg-secondary-400 text-primary-900 rounded-none px-4 sm:px-6 py-3 text-xs font-bold uppercase tracking-wider"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-none px-4 sm:px-6 py-3 text-xs font-bold uppercase tracking-wider"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'rfqs', label: 'Quote Requests', icon: FileText },
            { id: 'contacts', label: 'Messages', icon: Mail },
            { id: 'categories', label: 'Categories', icon: FolderOpen },
            { id: 'products', label: 'Products', icon: Package }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-[#0a1628] text-white'
                  : 'bg-white border border-border text-muted-foreground hover:text-primary-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Eye} title="Page Views" value={analytics.page_views.toLocaleString()} subtitle={`${analytics.visits_today} today`} />
              <StatCard icon={TrendingUp} title="Total Visits" value={analytics.total_visits.toLocaleString()} subtitle={`${analytics.visits_this_week} this week`} color="secondary" />
              <StatCard icon={FileText} title="Quote Requests" value={analytics.rfq_count} subtitle="Total submissions" />
              <StatCard icon={Mail} title="Messages" value={analytics.contact_count} subtitle="Contact messages" color="secondary" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-border p-6">
                <h3 className="text-lg font-heading font-bold text-primary-900 mb-6">Daily Page Views (Last 7 Days)</h3>
                {analytics.daily_visits.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.daily_visits.map((day, index) => {
                      const maxViews = Math.max(...analytics.daily_visits.map(d => d.views));
                      const percentage = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                      return (
                        <div key={index} className="flex items-center gap-4">
                          <span className="text-xs font-mono text-muted-foreground w-20">
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <div className="flex-1 h-8 bg-muted overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${percentage}%` }} transition={{ duration: 0.5, delay: index * 0.1 }} className="h-full bg-[#0a1628]" />
                          </div>
                          <span className="text-sm font-mono font-medium text-primary-900 w-12 text-right">{day.views}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No data available</p>
                )}
              </div>

              <div className="bg-white border border-border p-6">
                <h3 className="text-lg font-heading font-bold text-primary-900 mb-6">Top Pages</h3>
                {analytics.top_pages.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.top_pages.map((page, index) => (
                      <div key={index} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-[#0a1628] text-white text-xs font-bold flex items-center justify-center">{index + 1}</span>
                          <span className="text-sm font-medium text-primary-900">
                            {page.page === '/' ? 'Home' : page.page.replace('/', '').charAt(0).toUpperCase() + page.page.slice(2)}
                          </span>
                        </div>
                        <span className="text-sm font-mono text-muted-foreground">{page.views} views</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* RFQs Tab */}
        {activeTab === 'rfqs' && (
          <div className="bg-white border border-border overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border">
              <h3 className="text-lg font-heading font-bold text-primary-900">Quote Requests ({rfqs.length})</h3>
            </div>
            {rfqs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rfqs.map((rfq) => (
                      <tr key={rfq.id} className="hover:bg-muted/30">
                        <td className="px-4 py-4 text-sm text-muted-foreground whitespace-nowrap">{formatDate(rfq.created_at)}</td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-medium text-primary-900">{rfq.product_name}</p>
                          <p className="text-xs text-muted-foreground">{rfq.category}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-medium text-primary-900">{rfq.company_name}</p>
                          <p className="text-xs text-muted-foreground">{rfq.contact_name}</p>
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">{rfq.email}</td>
                        <td className="px-4 py-4 text-sm font-mono text-primary-900">{rfq.quantity} {rfq.unit.toUpperCase()}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium ${rfq.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {rfq.status === 'pending' ? <AlertCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                            {rfq.status === 'pending' ? 'Pending' : 'Responded'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No quote requests yet</p>
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-white border border-border overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border">
              <h3 className="text-lg font-heading font-bold text-primary-900">Contact Messages ({contacts.length})</h3>
            </div>
            {contacts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-muted/30">
                        <td className="px-4 py-4 text-sm text-muted-foreground whitespace-nowrap">{formatDate(contact.created_at)}</td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-medium text-primary-900">{contact.name}</p>
                          {contact.company && <p className="text-xs text-muted-foreground">{contact.company}</p>}
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">{contact.email}</td>
                        <td className="px-4 py-4"><p className="text-sm text-primary-900 max-w-xs truncate">{contact.subject}</p></td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium ${contact.status === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                            {contact.status === 'new' ? <AlertCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                            {contact.status === 'new' ? 'New' : 'Responded'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No messages yet</p>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="bg-white border border-border overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border flex justify-between items-center">
              <h3 className="text-lg font-heading font-bold text-primary-900">Categories ({categories.length})</h3>
              <Button
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ name: '', slug: '', description: '', image: '' });
                  setShowCategoryModal(true);
                }}
                className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-none px-4 py-2 text-xs font-bold uppercase tracking-wider"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {categories.map((cat) => (
                <div key={cat.id} className="border border-border p-4 hover:shadow-lg transition-shadow">
                  <img src={cat.image} alt={cat.name} className="w-full h-32 object-cover mb-4" />
                  <h4 className="font-heading font-bold text-primary-900">{cat.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{cat.description}</p>
                  <p className="text-xs text-secondary-600 mt-2">{cat.product_count} products</p>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingCategory(cat);
                        setCategoryForm({ name: cat.name, slug: cat.slug, description: cat.description, image: cat.image });
                        setShowCategoryModal(true);
                      }}
                      className="rounded-none text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="rounded-none text-xs text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white border border-border overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border flex justify-between items-center">
              <h3 className="text-lg font-heading font-bold text-primary-900">Products ({products.length})</h3>
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setProductForm({
                    name: '', slug: '', category_id: categories[0]?.id || '', description: '',
                    specifications: {}, images: [''], min_order: '', origin: '', certifications: []
                  });
                  setShowProductModal(true);
                }}
                className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-none px-4 py-2 text-xs font-bold uppercase tracking-wider"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Min Order</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Origin</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-muted/30">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {product.images?.[0] && (
                            <img src={product.images[0]} alt={product.name} className="w-12 h-12 object-cover" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-primary-900">{product.name}</p>
                            <p className="text-xs text-muted-foreground">{product.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">{product.category_name}</td>
                      <td className="px-4 py-4 text-sm font-mono text-primary-900">{product.min_order}</td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">{product.origin}</td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingProduct(product);
                              setProductForm({
                                name: product.name,
                                slug: product.slug,
                                category_id: product.category_id,
                                description: product.description,
                                specifications: JSON.stringify(product.specifications, null, 2),
                                images: product.images || [''],
                                min_order: product.min_order,
                                origin: product.origin,
                                certifications: product.certifications?.join(', ') || ''
                              });
                              setShowProductModal(true);
                            }}
                            className="rounded-none text-xs"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="rounded-none text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Category Modal */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCategoryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-heading font-bold text-primary-900">
                  {editingCategory ? 'Edit Category' : 'Add Category'}
                </h3>
                <button onClick={() => setShowCategoryModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Name</label>
                  <Input value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} className="rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Slug</label>
                  <Input value={categoryForm.slug} onChange={(e) => setCategoryForm({...categoryForm, slug: e.target.value})} className="rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Description</label>
                  <textarea value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})} className="w-full border border-border p-3 text-sm" rows={3} />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Image URL</label>
                  <Input value={categoryForm.image} onChange={(e) => setCategoryForm({...categoryForm, image: e.target.value})} className="rounded-none" />
                </div>
              </div>
              <div className="p-6 border-t border-border flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowCategoryModal(false)} className="rounded-none">Cancel</Button>
                <Button onClick={handleSaveCategory} className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-none">Save Category</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Modal */}
      <AnimatePresence>
        {showProductModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowProductModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-heading font-bold text-primary-900">
                  {editingProduct ? 'Edit Product' : 'Add Product'}
                </h3>
                <button onClick={() => setShowProductModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Name</label>
                    <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} className="rounded-none" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Slug</label>
                    <Input value={productForm.slug} onChange={(e) => setProductForm({...productForm, slug: e.target.value})} className="rounded-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Category</label>
                  <select 
                    value={productForm.category_id} 
                    onChange={(e) => setProductForm({...productForm, category_id: e.target.value})} 
                    className="w-full border border-border p-3 text-sm"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Description</label>
                  <textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} className="w-full border border-border p-3 text-sm" rows={3} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Min Order</label>
                    <Input value={productForm.min_order} onChange={(e) => setProductForm({...productForm, min_order: e.target.value})} placeholder="e.g., 500 MT" className="rounded-none" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Origin</label>
                    <Input value={productForm.origin} onChange={(e) => setProductForm({...productForm, origin: e.target.value})} placeholder="e.g., India, UAE" className="rounded-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Images (one URL per line)</label>
                  <textarea 
                    value={Array.isArray(productForm.images) ? productForm.images.join('\n') : productForm.images} 
                    onChange={(e) => setProductForm({...productForm, images: e.target.value.split('\n')})} 
                    className="w-full border border-border p-3 text-sm font-mono" 
                    rows={3} 
                    placeholder="https://example.com/image1.jpg"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Certifications (comma separated)</label>
                  <Input 
                    value={Array.isArray(productForm.certifications) ? productForm.certifications.join(', ') : productForm.certifications} 
                    onChange={(e) => setProductForm({...productForm, certifications: e.target.value})} 
                    placeholder="ISO 9001, HACCP, SGS" 
                    className="rounded-none" 
                  />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Specifications (JSON)</label>
                  <textarea 
                    value={typeof productForm.specifications === 'string' ? productForm.specifications : JSON.stringify(productForm.specifications, null, 2)} 
                    onChange={(e) => setProductForm({...productForm, specifications: e.target.value})} 
                    className="w-full border border-border p-3 text-sm font-mono" 
                    rows={4} 
                    placeholder='{"Grade": "Premium", "Moisture": "12%"}'
                  />
                </div>
              </div>
              <div className="p-6 border-t border-border flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowProductModal(false)} className="rounded-none">Cancel</Button>
                <Button onClick={handleSaveProduct} className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-none">Save Product</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Admin;
